import React from 'react';
import styled from '@emotion/styled';
import { rem } from 'polished';
import { getTrackingValues } from '../../../core/utils/Utils';
import {
  colors,
  animations,
  text,
  shadows,
  spaces,
} from '../../../core/styles/theme/default';
import Image from '../../Image';
import { CardProps } from '../Card';

const StyledImageWrap = styled.div`
  position: relative;
  height: 0;
  overflow: hidden;
  padding-bottom: 56.25%;
  background: white;
`;

const StyledImage = styled(Image)`
  object-fit: cover;
  position: absolute;
  width: 100%;
`;

const StyledTextWrap = styled.div`
  padding: ${rem(spaces.epsilon)} ${rem(spaces.epsilon)} ${rem(spaces.zeta)};
`;

const StyledTitle = styled.div`
  ${text.h3};
  font-size: ${rem(20)};
  margin-bottom: ${rem(spaces.beta)};
`;

const StyledSubtitle = styled.div`
  ${text.p3};
  margin-bottom: ${rem(spaces.delta)};
`;

const StyledText = styled.div`
  ${text.p4};
  color: ${colors.grey700};
`;

const StyledLink = styled.a`
  text-decoration: none;
  color: ${colors.grey900};
`;

const StyledCard = styled.div`
  height: 100%;
  border-radius: ${rem(8)};
  box-shadow: ${shadows.ctaBlueHover};
  transition: transform ${animations.default};
  overflow: hidden;

  &:hover {
    transform: translateY(-${rem(spaces.delta)});
  }
`;

const CardContent = ({ title, subtitle, text, image }: CardProps) => {
  const hasContent = title || subtitle || text;

  return (
    <StyledCard>
      {image && (
        <StyledImageWrap>
          <StyledImage src={image.src} alt={image.alt} />
        </StyledImageWrap>
      )}
      {hasContent && (
        <StyledTextWrap>
          {title && <StyledTitle>{title}</StyledTitle>}
          {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
          {text && <StyledText>{text}</StyledText>}
        </StyledTextWrap>
      )}
    </StyledCard>
  );
};

const DefaultCard = ({ link, tracking, ...rest }: CardProps) => {
  const trackingProps = getTrackingValues({ tracking });

  return link ? (
    <StyledLink
      href={link.href}
      target={link.target}
      title={link.title}
      {...trackingProps}
    >
      <CardContent {...rest} />
    </StyledLink>
  ) : (
    <CardContent {...rest} />
  );
};

export default DefaultCard;
